import * as i0 from '@angular/core';
import { InjectionToken, Component, ChangeDetectionStrategy, Inject, Input, LOCALE_ID, Injectable, NgModule } from '@angular/core';
import * as i1 from '@angular/router';
import { NavigationEnd, RouterModule } from '@angular/router';
import { Subject, Observable } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i3 from '@simpl/siemens-brand-localize';
import { SiLocalizationModule } from '@simpl/siemens-brand-localize';
import { SiLink } from '@simpl/siemens-brand-ng/models';

const SI_BREADCRUMB_RESOLVER_SERVICE = new InjectionToken('si.breadcrumb.resolver.service');

/**
 * @license
 * Copyright Siemens AG, 2017
 * Licensed under the Siemens Inner Source License 1.1
 */
class SiBreadcrumbComponent {
    constructor(route, router, resolverService, changeDetector) {
        this.route = route;
        this.router = router;
        this.resolverService = resolverService;
        this.changeDetector = changeDetector;
        /**
         * If enabled breadcrumbs will not be displayed if current url is root (links.length === 0)
         */
        this.hideOnRootPath = false;
        this.displayHome = false;
        this.nextRoute = new Subject();
        this.trackByIndex = (index) => index;
        this.links = new Array();
    }
    ngOnInit() {
        this.routerSubscription = this.router.events
            // This seems to be a TS bug. It would expect that typecast of function is not necessary.
            // Check if this is fixed with ts versions > 4.0.2
            .pipe(filter((e => e instanceof NavigationEnd)))
            .subscribe(navigationEvent => {
            // Get the new url
            const newUrl = navigationEvent.urlAfterRedirects || navigationEvent.url;
            // Only update when url differs from previous url
            if (this.currentUrl !== newUrl) {
                this.currentUrl = newUrl;
                this.nextRoute.next();
                this.computePath();
                this.changeDetector.markForCheck();
            }
        });
        if (this.router.navigated) {
            this.currentUrl = this.router.url;
            this.computePath();
        }
    }
    ngOnDestroy() {
        this.routerSubscription?.unsubscribe();
        this.nextRoute.next();
    }
    computePath() {
        // Get a snapshot of the all current activate routes
        const pathFromRoot = this.route.snapshot
            .pathFromRoot;
        // Find the child/leaf route that fits to the url
        const route = this.findRouteWithUrl(pathFromRoot, this.currentUrl.split('?')[0]);
        if (route) {
            // Workaround to fix a bug that the route is null, in some cases
            const links$ = this.resolverService.resolve(route);
            if (links$ instanceof Observable) {
                links$.pipe(takeUntil(this.nextRoute)).subscribe(links => {
                    this.links = links;
                    this.changeDetector.markForCheck();
                });
            }
            else {
                this.links = links$;
            }
        }
    }
    findRouteWithUrl(routes, url) {
        let result = null;
        for (const route of routes) {
            const routeUrl = this.getUrl(route);
            if (url === routeUrl && !route.data?.siBreadcrumbIgnore) {
                result = route;
                break;
            }
            else {
                result = this.findRouteWithUrl(route.children, url);
                if (result != null) {
                    break;
                }
            }
        }
        return result;
    }
    getUrl(route) {
        const parent = route.parent;
        let url;
        if (parent != null) {
            url = this.getUrl(parent);
        }
        else {
            url = '';
        }
        const myUrl = route.url.map(o => o.toString()).join('/');
        if (!url.endsWith('/')) {
            url = url + '/';
        }
        url = url + myUrl;
        return url;
    }
}
SiBreadcrumbComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiBreadcrumbComponent, deps: [{ token: i1.ActivatedRoute }, { token: i1.Router }, { token: SI_BREADCRUMB_RESOLVER_SERVICE }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
SiBreadcrumbComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiBreadcrumbComponent, selector: "si-breadcrumb", inputs: { hideOnRootPath: "hideOnRootPath" }, ngImport: i0, template: "<ng-container *ngIf=\"!hideOnRootPath || links.length !== 0\">\n    <nav [siLocalizeAttribute]=\"{'aria-label': 'si-breadcrumb.aria-label'}\">\n        <div class=\"row\">\n            <div class=\"col-md-12\">\n                <ol class=\"breadcrumb\">\n                    <li class=\"breadcrumb-item\">\n                        <a routerLink=\"/\" [siLocalizeAttribute]=\"{'aria-label': 'si-breadcrumb.home.aria-label'}\">\n                            <span class=\"siemens-home\"></span>\n                        </a>\n                    </li>\n                    <li *ngFor=\"let link of links; trackBy: trackByIndex\" class=\"breadcrumb-item\">\n                        <a [routerLink]=\"link.url ? link.url : currentUrl\"\n                           [attr.aria-current]=\"links.indexOf(link) === links.length - 1 ? 'page' : null\">\n                            <span>{{link.name}}</span>\n                        </a>\n                    </li>\n                </ol>\n            </div>\n        </div>\n    </nav>\n</ng-container>\n", dependencies: [{ kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "directive", type: i3.SiLocalizeAttributeDirective, selector: "[siLocalizeAttribute]", inputs: ["siLocalizeAttribute"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiBreadcrumbComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-breadcrumb', changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-container *ngIf=\"!hideOnRootPath || links.length !== 0\">\n    <nav [siLocalizeAttribute]=\"{'aria-label': 'si-breadcrumb.aria-label'}\">\n        <div class=\"row\">\n            <div class=\"col-md-12\">\n                <ol class=\"breadcrumb\">\n                    <li class=\"breadcrumb-item\">\n                        <a routerLink=\"/\" [siLocalizeAttribute]=\"{'aria-label': 'si-breadcrumb.home.aria-label'}\">\n                            <span class=\"siemens-home\"></span>\n                        </a>\n                    </li>\n                    <li *ngFor=\"let link of links; trackBy: trackByIndex\" class=\"breadcrumb-item\">\n                        <a [routerLink]=\"link.url ? link.url : currentUrl\"\n                           [attr.aria-current]=\"links.indexOf(link) === links.length - 1 ? 'page' : null\">\n                            <span>{{link.name}}</span>\n                        </a>\n                    </li>\n                </ol>\n            </div>\n        </div>\n    </nav>\n</ng-container>\n" }]
        }], ctorParameters: function () { return [{ type: i1.ActivatedRoute }, { type: i1.Router }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [SI_BREADCRUMB_RESOLVER_SERVICE]
                }] }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { hideOnRootPath: [{
                type: Input
            }] } });

class SiBreadcrumbDefaultResolverService {
    constructor(locale) {
        this.locale = locale;
    }
    resolve(route) {
        if (route.data.siBreadcrumb || route.data.siRawBreadcrumb) {
            return this.resolveCustomRoutePart(route);
        }
        else {
            return this.resolveDefault(route);
        }
    }
    resolveCustomRoutePart(route) {
        if (route.data.siBreadcrumb) {
            return route.data.siBreadcrumb;
        }
        else if (route.data.siRawBreadcrumb) {
            const rawLinks = route.data.siRawBreadcrumb;
            return rawLinks.map(rl => new SiLink(this.calculateName(route, rl.name), rl.url ? this.calculateUrl(route, rl.url) : route.fragment, rl.iconClass));
        }
    }
    resolveDefault(route) {
        const links = [];
        let currRoute = route;
        while (currRoute != null) {
            if (currRoute.data.siBreadcrumb || currRoute.data.siRawBreadcrumb) {
                links.splice(0, 0, ...this.resolveCustomRoutePart(currRoute));
            }
            else if (currRoute.url.length > 0) {
                const routeUrl = this.getUrl(currRoute);
                const routeName = this.getName(currRoute);
                let link;
                if (links.length === 0) {
                    link = new SiLink(routeName);
                }
                else {
                    link = new SiLink(routeName, routeUrl);
                }
                links.splice(0, 0, link);
            }
            currRoute = currRoute.parent;
        }
        return links;
    }
    getUrl(route) {
        const parent = route.parent;
        let url;
        if (parent != null) {
            url = this.getUrl(parent);
        }
        else {
            url = '';
        }
        const myUrl = route.url.map(o => o.toString()).join('/');
        if (!url.endsWith('/')) {
            url = url + '/';
        }
        url = url + myUrl;
        return url;
    }
    getName(route) {
        let name = route.data.title || route.url[0].path;
        if (typeof name === 'object') {
            name = name[this.locale];
        }
        return this.calculateName(route, name);
    }
    calculateName(route, baseName) {
        return this.calculate(route, baseName, /{(\w+)}/g);
    }
    calculateUrl(route, baseUrl) {
        return this.calculate(route, baseUrl, /:(\w+)\/?/g, ':', '');
    }
    calculate(route, base, pattern, replaceStart = '{', replaceEnd = '}') {
        let name = base;
        const values = [];
        let finding;
        // tslint:disable-next-line:no-conditional-assignment
        while ((finding = pattern.exec(name)) != null) {
            values.push(finding[1]);
        }
        values.forEach(value => {
            let replace = this.findParam(route, value);
            if (route.data.replaceValues) {
                replace = route.data.replaceValues[replace] || replace;
            }
            name = name.replace(`${replaceStart}${value}${replaceEnd}`, replace);
        });
        return name;
    }
    findParam(route, paramKey) {
        return (route.paramMap.get(paramKey) ??
            (route.parent ? this.findParam(route.parent, paramKey) : paramKey));
    }
}
SiBreadcrumbDefaultResolverService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiBreadcrumbDefaultResolverService, deps: [{ token: LOCALE_ID }], target: i0.ɵɵFactoryTarget.Injectable });
SiBreadcrumbDefaultResolverService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiBreadcrumbDefaultResolverService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiBreadcrumbDefaultResolverService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [LOCALE_ID]
                }] }]; } });

class SiBreadcrumbModule {
}
SiBreadcrumbModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiBreadcrumbModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiBreadcrumbModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiBreadcrumbModule, declarations: [SiBreadcrumbComponent], imports: [CommonModule, RouterModule, SiLocalizationModule], exports: [SiBreadcrumbComponent] });
SiBreadcrumbModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiBreadcrumbModule, providers: [{
            provide: SI_BREADCRUMB_RESOLVER_SERVICE, useClass: SiBreadcrumbDefaultResolverService,
        }], imports: [CommonModule, RouterModule, SiLocalizationModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiBreadcrumbModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiBreadcrumbComponent],
                    imports: [CommonModule, RouterModule, SiLocalizationModule],
                    exports: [SiBreadcrumbComponent],
                    providers: [{
                            provide: SI_BREADCRUMB_RESOLVER_SERVICE, useClass: SiBreadcrumbDefaultResolverService,
                        }],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SI_BREADCRUMB_RESOLVER_SERVICE, SiBreadcrumbComponent, SiBreadcrumbDefaultResolverService, SiBreadcrumbModule };
